"use client";

import { useMemo, useState } from "react";
import type { Ability, Comprehension, SessionHour, SessionPreferences } from "@/lib/session-planner/types";
import type { SessionContext } from "@/lib/session-planner/comfort";
import {
  buildHeatmap,
  buildSupProfileQualityHeatmap,
  type HeatCell,
  type HeatLens,
  type SupProfileQualityHeatCell,
  type SupProfileQualityHeatmap,
} from "@/lib/session-planner/heatmap";
import { hourSummary, windQualityChip } from "@/lib/session-planner/explain";
import { scoreColour, NA_COLOUR } from "@/lib/conditions/score-colour";
import { scoreOutOf100 } from "@/lib/conditions/score-display";
import type { SupProfileSessionHour } from "@/lib/session-planner/api-types";
import { prefScoringKey } from "@/lib/session-planner/preferences";
import { isSupProfileKey } from "@/lib/conditions/sport-profiles";

const NIGHT = "#EEF0F2";
const TOO_STRONG = "#E4B7B1"; // desaturated red
const TOO_WEAK = "#BBD4E6"; // desaturated blue

const ABILITY_RAMP: Record<Ability, string> = {
  beginner: "#86B348",
  intermediate: "#3E9B3E",
  advanced: "#2F7DB0",
  expert: "#1E3A8A",
};
const ABILITY_LABEL: Record<Ability, string> = {
  beginner: "Beginner-friendly",
  intermediate: "Intermediate+",
  advanced: "Advanced+",
  expert: "Expert only",
};

// The "By level" lens is only meaningful where ability drives the comfort band.
// For swim (swimCore ignores ability) and SUP (supCore bands on wind/water-state)
// every ability resolves to the same band, so the lens collapses — hide it there.
const LEVEL_LENS_SPORTS = new Set(["surf", "bodyboarding", "wind", "foil", "sailing"]);

function pad(h: number): string {
  return `${String(h).padStart(2, "0")}:00`;
}

function fitBg(cell: HeatCell): { background: string; opacity: number } {
  if (!cell.isDay || !cell.verdict) return { background: NIGHT, opacity: 1 };
  const v = cell.verdict;
  if (v.excludedBy === "timing" || v.excludedBy === "water temp" || v.excludedBy === "water quality") {
    return { background: NA_COLOUR, opacity: 1 };
  }
  if (v.band === "too-strong") return { background: TOO_STRONG, opacity: 1 };
  if (v.band === "too-weak") return { background: TOO_WEAK, opacity: 1 };
  // acceptable: score colour, shaded by preference fit (0.4..1.2 -> 0.55..0.98)
  return { background: scoreColour(cell.score), opacity: Math.max(0.55, Math.min(1, 0.5 + (v.fitMultiplier - 0.4) * 0.6)) };
}

function levelBg(cell: HeatCell): { background: string; opacity: number } {
  if (!cell.isDay || !cell.verdict) return { background: NIGHT, opacity: 1 };
  if (cell.tooBigForAll) return { background: TOO_STRONG, opacity: 1 };
  if (!cell.suitsFrom) return { background: NA_COLOUR, opacity: 1 };
  return { background: ABILITY_RAMP[cell.suitsFrom], opacity: 1 };
}

function decidingFactor(cell: HeatCell): string {
  if (!cell.isDay || !cell.verdict) return "Outside daylight";
  const v = cell.verdict;
  switch (v.excludedBy) {
    case "timing": return "Outside your preferred timing";
    case "water temp": return "Below your comfort temperature";
    case "water quality": return "Water quality flagged";
    case "wave size": return "Bigger than your level";
    case "wind": return "Wind out of range for your level";
    case "swell": return "Swell out of range for your level";
    default:
      if (v.band === "too-strong") return "Bigger than your level";
      if (v.band === "too-weak") return "Too small to bother";
      return "In range for you";
  }
}

type Props = {
  hours: readonly SessionHour[] | readonly SupProfileSessionHour[];
  prefs: SessionPreferences;
  ctx: SessionContext;
  comprehension: Comprehension;
};

export function SessionHeatmap({ hours, prefs, ctx, comprehension }: Props) {
  const [lens, setLens] = useState<HeatLens>("fit");
  const [selected, setSelected] = useState<{ dayOffset: 0 | 1; hour: number } | null>(null);

  const namedSupProfile = isSupProfileKey(prefScoringKey(prefs));
  const namedModel = useMemo(
    () => namedSupProfile
      ? buildSupProfileQualityHeatmap(hours as readonly SupProfileSessionHour[])
      : null,
    [hours, namedSupProfile],
  );
  const model = useMemo(
    () => namedSupProfile
      ? null
      : buildHeatmap(hours as readonly SessionHour[], prefs, ctx),
    [hours, namedSupProfile, prefs, ctx],
  );

  if (namedModel) {
    return (
      <SupProfileQualityHeatmapView
        model={namedModel}
        selected={selected}
        onSelect={setSelected}
      />
    );
  }
  if (!model) return null;
  const rows = model.rows.filter((r) => r.cells.some((c) => c.isDay));
  if (rows.length === 0 || model.hours.length === 0) return null;

  // Force "My fit" when the level lens is hidden (covers switching sport while a
  // stale "level" selection is held in state).
  const levelLensAvailable = LEVEL_LENS_SPORTS.has(prefs.sport);
  const activeLens: HeatLens = levelLensAvailable ? lens : "fit";
  const anyAcceptable = rows.some((r) => r.cells.some((c) => c.isDay && c.verdict?.acceptable));

  const selectedCell: HeatCell | undefined = selected
    ? rows.find((r) => r.dayOffset === selected.dayOffset)?.cells.find((c) => c.hour === selected.hour)
    : undefined;
  const legacyHours = hours as readonly SessionHour[];
  const selectedHour: SessionHour | undefined = selected
    ? legacyHours.find((h) => h.dayOffset === selected.dayOffset && h.hour === selected.hour)
    : undefined;

  return (
    <div className="rounded-[14px] border border-border-light bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h4 className="font-serif text-[15px] font-bold text-navy">Next two days, hour by hour</h4>
        {levelLensAvailable ? (
          <div className="flex shrink-0 rounded-full border border-border-light p-0.5 text-[12px] font-semibold">
            {(["fit", "level"] as HeatLens[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLens(l)}
                aria-pressed={lens === l}
                className={`whitespace-nowrap rounded-full px-2.5 py-1 ${lens === l ? "bg-navy text-white" : "text-text-muted"}`}
              >
                {l === "fit" ? "My fit" : "By level"}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-[2px]">
          <thead>
            <tr>
              <th className="w-12" />
              {model.hours.map((hr, idx) => (
                <th key={hr} className="text-[9px] font-medium text-text-muted">
                  {idx % 3 === 0 ? pad(hr).slice(0, 2) : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.dayOffset}>
                <td className="pr-2 text-right text-[11px] font-semibold text-text-muted">{row.label}</td>
                {row.cells.map((cell) => {
                  const style = activeLens === "fit" ? fitBg(cell) : levelBg(cell);
                  const isSel = selected?.dayOffset === cell.dayOffset && selected?.hour === cell.hour;
                  return (
                    <td key={`${cell.dayOffset}-${cell.hour}`} className="p-0">
                      <button
                        type="button"
                        onClick={() => setSelected({ dayOffset: cell.dayOffset, hour: cell.hour })}
                        title={`${pad(cell.hour)} — ${decidingFactor(cell)}`}
                        aria-label={`${row.label} ${pad(cell.hour)}: ${decidingFactor(cell)}`}
                        className={`block h-6 w-full rounded-[3px] ${isSel ? "ring-2 ring-navy" : ""}`}
                        style={{ backgroundColor: style.background, opacity: style.opacity }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedCell && selectedHour ? (
        <p className="mt-3 text-[12.5px] leading-relaxed text-text-body">
          <span className="font-semibold text-navy">
            {rows.find((r) => r.dayOffset === selected!.dayOffset)!.label} {pad(selected!.hour)}
          </span>{" "}
          — {decidingFactor(selectedCell)}.{" "}
          {selectedCell.isDay ? hourSummary(prefs, selectedHour, comprehension, ctx) : ""}
          {(() => {
            const chip = windQualityChip(selectedHour.windDirDeg, ctx.beachFacingDeg ?? null);
            return selectedCell.isDay && chip ? ` · ${chip.label}` : "";
          })()}
        </p>
      ) : (
        <p className="mt-3 text-[12px] text-text-muted">
          {activeLens === "fit"
            ? anyAcceptable
              ? "Coloured by how well each hour meets your preferences. Tap an hour for detail."
              : "Nothing clears the bar at your level over the next two days — tap an hour to see why."
            : "Coloured by who the conditions suit, ignoring your timing/temperature. Tap an hour for detail."}
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-text-muted">
        {activeLens === "fit" ? (
          <>
            <Legend swatch={scoreColour(8)} label="Great" />
            <Legend swatch={scoreColour(5.5)} label="OK" />
            <Legend swatch={TOO_STRONG} label="Too big" />
            <Legend swatch={TOO_WEAK} label="Too small" />
            <Legend swatch={NA_COLOUR} label="Excluded" />
          </>
        ) : (
          <>
            {(Object.keys(ABILITY_RAMP) as Ability[]).map((a) => (
              <Legend key={a} swatch={ABILITY_RAMP[a]} label={ABILITY_LABEL[a]} />
            ))}
            <Legend swatch={TOO_STRONG} label="Too big for all" />
          </>
        )}
      </div>
    </div>
  );
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-[2px]" style={{ backgroundColor: swatch }} aria-hidden />
      {label}
    </span>
  );
}

function supQualityLabel(cell: SupProfileQualityHeatCell): string {
  if (cell.state === "unavailable" || cell.score === null) {
    return "Quality unavailable";
  }
  return cell.confidence === "partial"
    ? `Quality ${scoreOutOf100(cell.score)}/100, partial forecast evidence`
    : `Quality ${scoreOutOf100(cell.score)}/100`;
}

function SupProfileQualityHeatmapView({
  model,
  selected,
  onSelect,
}: {
  model: SupProfileQualityHeatmap;
  selected: { dayOffset: 0 | 1; hour: number } | null;
  onSelect: (next: { dayOffset: 0 | 1; hour: number }) => void;
}) {
  const rows = model.rows.filter((row) => row.cells.some((cell) => cell.isDay));
  if (rows.length === 0 || model.hours.length === 0) return null;
  const selectedCell = selected
    ? rows.find((row) => row.dayOffset === selected.dayOffset)?.cells.find(
        (cell) => cell.hour === selected.hour,
      )
    : undefined;

  return (
    <div className="rounded-[14px] border border-border-light bg-white p-4">
      <h4 className="mb-3 font-serif text-[15px] font-bold text-navy">
        Next two days, hour by hour
      </h4>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-[2px]">
          <thead>
            <tr>
              <th className="w-12" />
              {model.hours.map((hour, index) => (
                <th key={hour} className="text-[9px] font-medium text-text-muted">
                  {index % 3 === 0 ? pad(hour).slice(0, 2) : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.dayOffset}>
                <td className="pr-2 text-right text-[11px] font-semibold text-text-muted">
                  {row.label}
                </td>
                {row.cells.map((cell) => {
                  const label = supQualityLabel(cell);
                  const active = selected?.dayOffset === cell.dayOffset &&
                    selected.hour === cell.hour;
                  return (
                    <td key={`${cell.dayOffset}-${cell.hour}`} className="p-0">
                      <button
                        type="button"
                        onClick={() => onSelect({
                          dayOffset: cell.dayOffset,
                          hour: cell.hour,
                        })}
                        title={`${pad(cell.hour)}: ${label}`}
                        aria-label={`${row.label} ${pad(cell.hour)}: ${label}`}
                        className={`block min-h-[44px] min-w-[44px] w-full rounded-[3px] ${active ? "ring-2 ring-navy" : ""}`}
                        style={{
                          backgroundColor: cell.score === null
                            ? NA_COLOUR
                            : scoreColour(cell.score),
                          opacity: cell.confidence === "partial" ? 0.75 : 1,
                        }}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12px] text-text-muted">
        {selectedCell
          ? `${rows.find((row) => row.dayOffset === selected!.dayOffset)?.label} ${pad(selectedCell.hour)}: ${supQualityLabel(selectedCell)}.`
          : "Headline quality stays independent of your personal fit. Tap an hour for evidence."}
      </p>
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px] text-text-muted">
        <Legend swatch={scoreColour(8)} label="Available quality" />
        <Legend swatch={NA_COLOUR} label="Quality unavailable" />
      </div>
    </div>
  );
}
